/**
 * Modo NATIVE del cierre del alta IT (P1a). Un lead born-native (alta del caller
 * comercial, sin `contact_lead`) no vive en el monolito: su flujo de cliente
 * (biometría, firma, datos) va al gateway native, no a `REACT_APP_CUSTOMER_APP_URL`.
 *
 * La señal llega en el MAGIC LINK que envía el flujo native (`?native=1`); se
 * persiste en `sessionStorage` para sobrevivir a la navegación del funnel. La
 * seguridad NO descansa en este flag (un param falseado solo cambia la base URL):
 * el gateway valida el token de lead y ms-agl es la fuente de verdad.
 *
 * DISABLED-BY-DEFAULT: sin `REACT_APP_NATIVE_GATEWAY_URL` configurada el modo
 * native nunca se activa, aunque venga el param — el código queda inerte.
 *
 * El flag de `sessionStorage` es POR PESTAÑA, pero la sesión (el token de
 * `useCache("token")`, en `localStorage`) no: una pestaña nueva o un navegador
 * reabierto seguía logueado y mandaba sus llamadas al monolito, que da 500 con un
 * alta native (30-oct-2026: status/documentos/checkout de samereu y un «llámame»
 * que no avisó a los asesores). Así que también es native la sesión cuyo token
 * emitió el gateway ({@link isGatewaySessionToken}).
 */
const PARAM = 'native';
const STORAGE_KEY = 'l5_native_mode';
/** Clave de `useCache("token")` (GetLifeContext): el token de la sesión del lead. */
const SESSION_TOKEN_KEY = 'token';

export function nativeGatewayBaseUrl(): string {
  return (process.env.REACT_APP_NATIVE_GATEWAY_URL ?? '').replace(/\/+$/, '');
}

/** true solo si hay gateway native configurado (si no, todo el modo es inerte). */
function nativeAvailable(): boolean {
  return '' !== nativeGatewayBaseUrl();
}

function readFlag(): boolean {
  try {
    return '1' === sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return false;
  }
}

function persistFlag(): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, '1');
  } catch {
    /* sessionStorage no disponible: el modo dura solo esta carga */
  }
}

/**
 * Detecta el modo native del magic link y lo persiste. Se llama al arrancar la
 * app. Devuelve el estado efectivo (param o ya persistido), siempre gated por
 * que exista el gateway native.
 */
export function detectNativeMode(search: string = globalThis.location?.search ?? ''): boolean {
  if (!nativeAvailable()) {
    return false;
  }
  try {
    const value = new URLSearchParams(search).get(PARAM);
    if ('1' === value || 'true' === value) {
      persistFlag();
      return true;
    }
  } catch {
    /* URL malformada: cae al flag persistido */
  }
  return readFlag() || isGatewaySessionToken(storedSessionToken());
}

/** Claims de un JWT, SIN validar la firma: solo deciden la base URL (la seguridad la pone el gateway). */
function jwtClaims(token: unknown): Record<string, unknown> | null {
  if ('string' !== typeof token) {
    return null;
  }
  const parts = token.split('.');
  if (3 !== parts.length) {
    return null;
  }
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const claims: unknown = JSON.parse(atob(base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')));
    return null !== claims && 'object' === typeof claims && !Array.isArray(claims)
      ? (claims as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

/**
 * ¿Token de sesión emitido por el gateway native? El gateway firma `{ulid, iat, exp}`
 * (LeadTokenIssuer: login, Google, organic, switch); el monolito (lexik) añade
 * siempre `roles`. Una sesión legacy de app.life5.it (alta cuyo JIT abortó) lleva
 * el token del monolito y sigue contra el monolito.
 */
export function isGatewaySessionToken(token: unknown): boolean {
  const claims = jwtClaims(token);
  return null !== claims && 'string' === typeof claims.ulid && '' !== claims.ulid && !('roles' in claims);
}

function storedSessionToken(): string {
  try {
    return localStorage.getItem(SESSION_TOKEN_KEY) ?? '';
  } catch {
    return '';
  }
}

/**
 * Estado efectivo del modo native (gated por gateway configurado): el flag de la
 * pestaña o una sesión del gateway, que sobrevive a pestañas nuevas y recargas.
 */
export function isNativeMode(): boolean {
  return nativeAvailable() && (readFlag() || isGatewaySessionToken(storedSessionToken()));
}

/**
 * Portal Cliente como punto de entrada único (2026-10-15): el cliente native
 * entra siempre al Dashboard al loguearse, tenga o no tenga lead a medias, y
 * ve ahí sus otras cotizaciones además de la póliza activa. DISABLED-BY-DEFAULT
 * (`REACT_APP_CUSTOMER_PROJECTS_ENABLED` ausente ⇒ inerte, igual que el resto
 * del modo native) hasta que el gateway sirva `GET
 * /customer/leads/{leadId}/dashboard/projects` (contrato pendiente, ver
 * docs-central agl/contratos/PROPUESTA_customer_self_service_projects_v1_2026-10-15.md).
 */
export function customerProjectsEnabled(): boolean {
  return isNativeMode() && 'true' === (process.env.REACT_APP_CUSTOMER_PROJECTS_ENABLED ?? '');
}

/**
 * Funnel ORGANIC native (F5a). El cliente organic llega por el calculador, NO
 * por un magic link, así que no hay `?native=1`: el calculador decide entrar en
 * native y lo persiste con {@link enterNativeMode}. Gate PROPIO
 * (`REACT_APP_ORGANIC_NATIVE`) separado del cierre born-native, para que
 * encender el gateway native (el flip) no active organic por accidente — organic
 * se enciende aparte, en coordinación con `ORGANIC_PUBLIC_ENABLED` del gateway.
 * Sigue gated por que exista el gateway (si no, inerte como todo el modo).
 */
export function organicNativeEnabled(): boolean {
  return nativeAvailable() && 'true' === (process.env.REACT_APP_ORGANIC_NATIVE ?? '');
}

/**
 * ¿Las consultas públicas del calculador (edad, capitales, email) van al gateway?
 * En el build native organic, SÍ desde la primera pantalla: el modo native solo se
 * activa al crear el lead ({@link enterNativeMode}), así que antes de eso esas
 * consultas iban al monolito (29-oct-2026, ~55 llamadas en 2 h desde
 * app.life5.it). Mismo criterio que el login y el OTP. Son rutas públicas sin
 * sesión, así que una sesión legacy en el build native tampoco se rompe.
 */
export function calculatorOnGateway(): boolean {
  return organicNativeEnabled() || isNativeMode();
}

/**
 * Entra en modo native sin magic link (lo usa el calculador organic cuando
 * {@link organicNativeEnabled}). Persiste el flag igual que el magic link; no
 * hace nada si no hay gateway configurado (el modo seguiría inerte).
 */
export function enterNativeMode(): void {
  if (!nativeAvailable()) {
    return;
  }
  persistFlag();
}

/** Base URL para las llamadas del cliente: gateway native en modo native, monolito si no. */
export function customerApiBaseUrl(): string {
  return isNativeMode()
    ? nativeGatewayBaseUrl()
    : `${process.env.REACT_APP_CUSTOMER_APP_URL ?? ''}`.replace(/\/+$/, '');
}

export function clearNativeMode(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* no-op */
  }
}

// Token del lead ORGANIC (F5a). En born-native el token llega por el magic link;
// en organic lo acuña el gateway en POST /organic/leads y el front lo guarda aquí
// para que el `getToken` native (que alimenta a setToken en QuotePage) lo
// devuelva, y de ahí lo lleven las llamadas a /customer/leads/*.
const ORGANIC_TOKEN_KEY = 'l5_native_organic_token';

export function storeOrganicToken(token: string): void {
  try {
    sessionStorage.setItem(ORGANIC_TOKEN_KEY, token);
  } catch {
    /* sin sessionStorage: el token dura solo esta carga (lo tiene quien lo pidió) */
  }
}

/**
 * El token del gateway con el que llamar a `/customer/leads/{lead}/*`. El de esta
 * pestaña (`sessionStorage`) o, en una pestaña nueva o tras un cambio de
 * preventivo, el de la sesión (`localStorage`). Con `leadUlid`, gana el que es de
 * ese lead (claim `ulid`): tras un switch el de la pestaña es del preventivo anterior.
 */
export function readOrganicToken(leadUlid?: string): string {
  let organic = '';
  try {
    organic = sessionStorage.getItem(ORGANIC_TOKEN_KEY) ?? '';
  } catch {
    /* sin sessionStorage: solo queda el de la sesión */
  }
  const stored = storedSessionToken();
  const session = isGatewaySessionToken(stored) ? stored : '';
  if (undefined !== leadUlid && '' !== leadUlid) {
    const wanted = leadUlid.toUpperCase();
    const own = [organic, session].find(
      (token) => isGatewaySessionToken(token) && wanted === String(jwtClaims(token)?.ulid).toUpperCase(),
    );
    if (undefined !== own) {
      return own;
    }
  }
  return '' !== organic ? organic : session;
}
